import React, { createContext, useContext, useRef, useState, useCallback } from "react";
import api from "../services/api";
import { isAuthenticated } from "../lib/auth";

const PlayerContext = createContext(null);
export const usePlayer = () => useContext(PlayerContext);

// Lecteur audio global avec file d'attente (queue) : joue une station ou une
// programmation (playlist), enregistre l'écoute dans l'historique si connecté.
export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const queueRef = useRef([]);       // liste courante
  const indexRef = useRef(0);        // position dans la liste
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [queueLen, setQueueLen] = useState(0);

  function ensureAudio() {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.addEventListener("pause", () => setPlaying(false));
      audioRef.current.addEventListener("playing", () => setPlaying(true));
      audioRef.current.addEventListener("ended", () => advance(1));
    }
    return audioRef.current;
  }

  const start = useCallback((station) => {
    if (!station || !station.url) return;
    const audio = ensureAudio();
    audio.src = station.url;
    audio.play().then(() => {
      setCurrent(station);
      setPlaying(true);
      if (isAuthenticated()) {
        api.post("/api/history", {
          station_uuid: station.station_uuid,
          name: station.name,
          url: station.url,
          favicon: station.favicon,
          country: station.country,
        }).catch(() => {});
      }
    }).catch(() => setPlaying(false));
  }, []);

  // Avance/recul dans la file (delta = +1 / -1)
  function advance(delta) {
    const q = queueRef.current;
    const next = indexRef.current + delta;
    if (next >= 0 && next < q.length) {
      indexRef.current = next;
      start(q[next]);
    }
  }

  const play = useCallback((station) => {
    if (!station || !station.url) return;
    // Re-clic sur la station en cours -> pause/reprise
    if (current && current.station_uuid === station.station_uuid) {
      const audio = audioRef.current;
      if (playing) { audio.pause(); setPlaying(false); }
      else { audio.play().then(() => setPlaying(true)).catch(() => {}); }
      return;
    }
    queueRef.current = [station];
    indexRef.current = 0;
    setQueueLen(1);
    start(station);
  }, [current, playing, start]);

  const playQueue = useCallback((list, startIndex = 0) => {
    const items = (list || []).filter((s) => s && s.url);
    if (items.length === 0) return;
    queueRef.current = items;
    indexRef.current = Math.min(Math.max(0, startIndex), items.length - 1);
    setQueueLen(items.length);
    start(items[indexRef.current]);
  }, [start]);

  const next = useCallback(() => advance(1), []);
  const prev = useCallback(() => advance(-1), []);

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !current) return;
    if (playing) { audio.pause(); setPlaying(false); }
    else { audio.play().then(() => setPlaying(true)).catch(() => {}); }
  }, [playing, current]);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) { audio.pause(); audio.src = ""; }
    queueRef.current = [];
    indexRef.current = 0;
    setQueueLen(0);
    setCurrent(null);
    setPlaying(false);
  }, []);

  return (
    <PlayerContext.Provider value={{ current, playing, queueLen, play, playQueue, toggle, next, prev, stop }}>
      {children}
    </PlayerContext.Provider>
  );
}
